import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Home,
  Users,
  PawPrint,
  CalendarPlus,
  Search,
  UserRound,
  Menu,
  X,
  LogIn,
  LogOut,
  CalendarDays,
  Scissors,
  MessageCircle,
} from "lucide-react";
import "../static/navigation.css";

function Navigation() {
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [responseMessage, setResponseMessage] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());

  const navItems = [
    { to: "/", label: "Dashboard", icon: Home, end: true },
    { to: "/Customers", label: "Customers", icon: Users },
    { to: "/leads", label: "Leads", icon: MessageCircle },
    { to: "/Pets", label: "Pets", icon: PawPrint },
    { to: "/MakeAppointment", label: "New Pet", icon: CalendarPlus },
    { to: "/add-appointment", label: "Search Pet", icon: Search },
    { to: "/staff", label: "Staff", icon: UserRound },
  ];

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const todayLabel = useMemo(() => {
    return currentTime.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
  }, [currentTime]);

  const timeLabel = useMemo(() => {
    return currentTime.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [currentTime]);

  const closeMobileMenu = () => {
    setIsOpen(false);
    setAccountOpen(false);
  };

  const fetchSuggestions = async (query) => {
    try {
      const response = await fetch(`/autocomplete_pet_phone/?query=${encodeURIComponent(query)}`);
      const data = await response.json();
      setSuggestions(data.suggestions || []);
    } catch (error) {
      console.error("Error fetching telephone:", error);
      setSuggestions([]);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhone(value);
    setResponseMessage("");

    if (value.trim().length > 2) {
      fetchSuggestions(value.trim());
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setPhone(suggestion.phone || "");
    setSuggestions([]);
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();

    if (!phone.trim()) {
      setResponseMessage("Enter a phone number first.");
      return;
    }

    try {
      const queryParams = new URLSearchParams({ phone: phone.trim() }).toString();
      const response = await fetch(`/fetch-pet/?${queryParams}`);

      if (!response.ok) {
        setResponseMessage("Pet not found.");
        return;
      }

      const data = await response.json();

      const query = new URLSearchParams({
        id: data.id || "",
        name: data.name || "",
        catdog: data.catdog || "",
        owner: data.owner || "",
        image: data.image || "",
        phone: data.phone || "",
        breed: data.breed || "",
        age: data.age?.toString() || "",
        behaviour: data.behaviour || "",
        allergies: data.allergies || "",
        vaccinated: data.vaccinated?.toString() || "",
        grooming_history: data.grooming_history || "",
        booking_date: data.booking_date || "",
        extra_info: data.extra_info || "",
        text: data.text || "",
      }).toString();

      closeMobileMenu();
      navigate(`/historygroom/?${query}`);
    } catch (error) {
      console.error("Error fetching pet:", error);
      setResponseMessage("Something went wrong. Try again.");
    }
  };

  return (
    <header className="pg-nav-shell">
      <nav className="pg-nav">
        <NavLink to="/" className="pg-nav-brand" onClick={closeMobileMenu}>
          <span className="pg-brand-logo">
            <img src={`${process.env.PUBLIC_URL}/icon/logo.png`} alt="Arys logo" />
          </span>
          <span className="pg-brand-text">
            <strong>Arys Grooming</strong>
            <small>Pet care dashboard</small>
          </span>
        </NavLink>

       <button
          type="button"
          className={`pg-nav-toggle ${isOpen ? "is-open" : ""}`}
          onClick={() => setIsOpen((prev) => !prev)}
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div className={`pg-nav-panel ${isOpen ? "is-open" : ""}`}>
          <div className="pg-nav-links">

            {navItems.map((item) => {
              const Icon = item.icon;

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={closeMobileMenu}
                  className={({ isActive }) =>
                    isActive ? "pg-nav-link active" : "pg-nav-link"
                  }
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
            <NavLink to="/quick-appointments" className="pg-nav-link">
              Quick Appointments
            </NavLink>
             <NavLink to="/staff-hr/" className="pg-nav-link">
              Staff HR
            </NavLink>
            <div className="pg-account">
              <button
                type="button"
                className="pg-nav-link pg-account-button"
                onClick={() => setAccountOpen((prev) => !prev)}
              >
                <UserRound size={18} />
                <span>Account</span>
              </button>

              <div className={`pg-account-menu ${accountOpen ? "is-open" : ""}`}>
                <NavLink to="/singin" onClick={closeMobileMenu}>
                  <LogIn size={16} />
                  Login
                </NavLink>
                <NavLink to="/logout" onClick={closeMobileMenu}>
                  <LogOut size={16} />
                  Logout
                </NavLink>
              </div>
            </div>
          </div>

          <form className="pg-pet-search" onSubmit={handleSearchSubmit}>
            <div className="pg-pet-search-box">
              <Search size={17} />
              <input
                type="text"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="Find pet by phone"
                autoComplete="off"
              />
              <button type="submit">Find</button>
            </div>

            {suggestions.length > 0 && (
              <div className="pg-suggestions">
                {suggestions.map((suggestion) => (
                  <button
                    type="button"
                    key={suggestion.id}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <PawPrint size={15} />
                    <span>
                      {suggestion.phone} — {suggestion.name}
                      {suggestion.owner_name ? ` (${suggestion.owner_name})` : ""}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {responseMessage && (
              <p className="pg-search-message">{responseMessage}</p>
            )}
          </form>

          <div className="pg-nav-meta">
            <div className="pg-meta-pill">
              <CalendarDays size={16} />
              <span>{todayLabel}</span>
            </div>
            <div className="pg-meta-pill dark">
              <Scissors size={16} />
              <span>{timeLabel}</span>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default Navigation;